import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import {
  createGlobalAiConversation,
  deleteGlobalAiConversation,
  GlobalAiConversationApiError,
  getGlobalAiConversation,
  listGlobalAiConversations,
  postGlobalAssistantGenerateTitle,
  updateGlobalAiConversation,
} from './global-ai-conversation-api';
import {
  fallbackConversationTitle,
  GLOBAL_AI_CONVERSATION_SCHEMA_VERSION,
  type GlobalAiConversationListItem,
  type GlobalAiConversationPayloadV1,
  parseConversationPayload,
  serializeConversation,
} from './global-ai-conversation-persistence';
import { useGlobalAiConversationStateStore } from './global-ai-conversation-state-store';

const CONVERSATIONS_QUERY_KEY = ['global-ai-conversations'] as const;

const EMPTY_CONVERSATIONS: GlobalAiConversationListItem[] = [];

export type GlobalAiConversationPersistenceApi = {
  conversations: GlobalAiConversationListItem[];
  isConversationListLoading: boolean;
  isConversationListError: boolean;
  refetchConversationList: () => void;
  /**
   * Clear the thread and detach from the persisted id (delegates to the
   * engine's resetSession; callers disable New Chat while a turn is
   * running, matching the drawer).
   */
  newChat: () => void;
  /** Fetch + hydrate a persisted conversation. Resolves ok=false on failure. */
  selectConversation: (id: string) => Promise<{
    ok: boolean;
    reason?: 'newer_schema' | 'malformed' | 'fetch';
  }>;
  deleteConversation: (id: string) => Promise<void>;
};

type HookArgs = {
  activeConversationId: string | null;
  setActiveConversationId: (value: string | null) => void;
  hydrateConversation: (
    payload: GlobalAiConversationPayloadV1,
    id: string
  ) => void;
};

/**
 * The ONE writer for conversation persistence. Mounted inside
 * `GlobalAIAssistantProvider` and exposed through the assistant context so
 * both skins (homepage sidebar, drawer history panel) share a single save
 * path. Autosaves at turn end (the engine store's derived turn-running
 * signal transitioning to idle — `endTurn` is its only producer),
 * reading the conversation-state store directly — synchronous and
 * mutually-consistent, so no commit-timing deferral is needed; a failed
 * autosave retries once and is otherwise silent — it must never block or
 * degrade the chat itself.
 */
export function useGlobalAiConversationPersistence(
  args: HookArgs
): GlobalAiConversationPersistenceApi {
  const queryClient = useQueryClient();
  const engineMounted = useGlobalAiEngineStore((s) => s.api !== null);

  // Latest active-id wiring for callbacks that fire outside React's render
  // cycle. Conversation CONTENT is read from the state store, not from here.
  const stateRef = useRef(args);
  stateRef.current = args;

  // Conversation ids known to exist server-side (POSTed by us, or loaded
  // via select) — decides POST vs PUT at save time.
  const persistedIdsRef = useRef<Set<string>>(new Set());
  // Conversation ids whose LLM title has already been requested. Separate from
  // `persistedIdsRef` because a title is generated once per conversation, while
  // that set is also populated by `selectConversation` (already titled) and
  // cleared by delete.
  const titledIdsRef = useRef<Set<string>>(new Set());

  const {
    data,
    isLoading: isConversationListLoading,
    isError: isConversationListError,
    refetch,
  } = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: ({ signal }) => listGlobalAiConversations(signal),
    enabled: engineMounted,
    staleTime: 30_000,
    meta: { skipGlobalErrorHandling: true },
  });
  // Stable while the query has no data — an inline `= []` default would mint
  // a fresh array every render and defeat the return-object memo below.
  const conversations = data ?? EMPTY_CONVERSATIONS;

  // Optimistic delete: the row disappears on click rather than after the
  // round-trip, and is restored if the DELETE fails (the caller surfaces the
  // error). `onSettled` reconciles with the server either way.
  const { mutateAsync: deleteMutation } = useMutation<
    void,
    Error,
    string,
    { previous?: GlobalAiConversationListItem[] }
  >({
    mutationFn: deleteGlobalAiConversation,
    onMutate: async (id) => {
      // Stop an in-flight list refetch from clobbering the optimistic write.
      await queryClient.cancelQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<GlobalAiConversationListItem[]>(
        CONVERSATIONS_QUERY_KEY
      );
      queryClient.setQueryData<GlobalAiConversationListItem[]>(
        CONVERSATIONS_QUERY_KEY,
        (old) => old?.filter((c) => c.id !== id)
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CONVERSATIONS_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    },
    meta: { skipGlobalErrorHandling: true },
  });

  const generateAndApplyTitle = useCallback(
    async (id: string, transcript: GlobalAiTranscriptMessage[]) => {
      const firstUser = transcript.find((m) => m.role === 'user');
      const firstAssistantText = transcript.find(
        (m): m is Extract<GlobalAiTranscriptMessage, { kind: 'text' }> =>
          m.role === 'assistant' && m.kind === 'text' && !m.ephemeral
      );
      if (!firstUser || !('text' in firstUser)) {
        return;
      }
      try {
        const { title } = await postGlobalAssistantGenerateTitle({
          prompt: firstUser.text,
          ...(firstAssistantText
            ? { assistantText: firstAssistantText.text }
            : {}),
        });
        if (title) {
          await updateGlobalAiConversation(id, { title });
          void queryClient.invalidateQueries({
            queryKey: CONVERSATIONS_QUERY_KEY,
          });
        }
      } catch {
        // Fallback title (set at create) stands; titling is best-effort.
      }
    },
    [queryClient]
  );

  const persistAfterTurn = useCallback(async () => {
    // The state store is written synchronously by every conversation
    // mutation, so this snapshot is current AND mutually consistent
    // (transcript + summary + scope + id advance together) — even though
    // this runs synchronously inside the engine's turn-end flip.
    const snapshot = useGlobalAiConversationStateStore.getState();
    const transcript = snapshot.transcript;
    // Nothing worth saving until a user turn exists (silent seeds excluded).
    if (!transcript.some((m) => m.role === 'user')) {
      return;
    }
    const id = snapshot.trackingId;
    const payload = serializeConversation({
      transcript,
      conversationSummary: snapshot.conversationSummary,
      sessionEntityScope: snapshot.sessionEntityScope,
      nowMs: Date.now(),
    });
    // Mark this conversation active only if the user hasn't switched away
    // while the (un-abortable) save was in flight. Without the re-check, a
    // POST for conversation A resolving after the user hydrated B would set
    // activeConversationId back to A — desyncing it from the displayed
    // transcript and wedging the sidebar (selectConversation short-circuits
    // on a matching active id, so A can't be re-opened).
    const markActiveIfStillCurrent = () => {
      if (useGlobalAiConversationStateStore.getState().trackingId === id) {
        stateRef.current.setActiveConversationId(id);
      }
    };
    const save = async () => {
      if (persistedIdsRef.current.has(id)) {
        await updateGlobalAiConversation(id, {
          payload,
          schemaVersion: GLOBAL_AI_CONVERSATION_SCHEMA_VERSION,
        });
        // Refresh the sidebar's updatedAt ordering.
        void queryClient.invalidateQueries({
          queryKey: CONVERSATIONS_QUERY_KEY,
        });
        return;
      }
      try {
        await createGlobalAiConversation({
          id,
          title: fallbackConversationTitle(transcript),
          schemaVersion: GLOBAL_AI_CONVERSATION_SCHEMA_VERSION,
          payload,
        });
      } catch (error) {
        // 409: a prior POST succeeded but its response was lost — the
        // conversation exists server-side. Converge to the PUT path so the
        // save doesn't loop as a permanent silent failure.
        if (
          !(
            error instanceof GlobalAiConversationApiError &&
            error.status === 409
          )
        ) {
          throw error;
        }
        await updateGlobalAiConversation(id, {
          payload,
          schemaVersion: GLOBAL_AI_CONVERSATION_SCHEMA_VERSION,
        });
      }
      // Both first-persist routes land here — a clean POST and the 409
      // convergence — because the 409 branch used to return early and skip
      // titling, leaving a conversation whose first POST response was lost on
      // its truncated-prompt fallback title forever.
      persistedIdsRef.current.add(id);
      markActiveIfStillCurrent();
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      // Title exactly once per conversation. Saves are fired un-awaited from
      // the store subscription, so two can overlap: both see an unpersisted
      // id, both POST, one 409s and converges, and both reach here — which
      // without this check is two LLM round-trips and two PUTs.
      if (!titledIdsRef.current.has(id)) {
        titledIdsRef.current.add(id);
        void generateAndApplyTitle(id, transcript);
      }
    };
    try {
      await save();
    } catch {
      // One silent retry; autosave must never surface as a chat error.
      try {
        await save();
      } catch (error) {
        console.warn('Global assistant conversation autosave failed', error);
        // The turn started an optimistic sidebar row that this failed save
        // never persisted server-side. Invalidate so the next background
        // refetch sweeps the orphan out instead of stranding it until the
        // next successful save.
        void queryClient.invalidateQueries({
          queryKey: CONVERSATIONS_QUERY_KEY,
        });
      }
    }
  }, [generateAndApplyTitle, queryClient]);

  const persistAfterTurnRef = useRef(persistAfterTurn);
  persistAfterTurnRef.current = persistAfterTurn;

  // Turn-end autosave: BOTH engine entry points (submitPrompt /
  // startAssistantTurn) call the store's turnId-keyed `endTurn` in their
  // `finally`, including error and stop paths, so the derived
  // running→not-running transition is the single reliable end-of-turn
  // signal — and `endTurn` is its only producer (cancelTurn only marks
  // `stopping`, which still reads as running, and a stale writer from a
  // replaced engine instance is a keyed no-op that never notifies). Engine
  // unmount clears `turn` via its store cleanup — saving there too is
  // correct (it captures a turn cut short by navigation to a hidden path).
  //
  // Saving synchronously inside the flip is safe: the conversation-state
  // store is written synchronously by every mutation, so persistAfterTurn's
  // snapshot is current and mutually consistent. (The old arm/defer/consume
  // machinery — pending ref, post-commit effect, macrotask fallback, and the
  // turn-start id guard — existed only because provider useState was
  // render-lagged at this exact moment.) A turn whose conversation was
  // switched away mid-flight saves the CURRENT post-switch state: after a
  // hydrate that's an idempotent PUT of just-loaded content; after New Chat
  // the empty transcript is skipped by the no-user-message guard.
  useEffect(() => {
    return useGlobalAiEngineStore.subscribe((state, prev) => {
      const wasRunning = selectIsTurnRunning(prev);
      const isRunning = selectIsTurnRunning(state);
      if (!wasRunning && isRunning) {
        // Optimistic sidebar row for a brand-new chat: named from the first
        // prompt — the exact fallback title the first save will persist —
        // and visible the moment the turn starts instead of after it ends.
        // (The engine appends the user message BEFORE flipping the flag, so
        // the store already holds the prompt here.) The LLM title upgrades
        // the name via the usual post-save PUT + refetch. Server refetches
        // reconcile the entry; a turn abandoned before its first save loses
        // the row on the next refetch. Silent seeded turns (no user message
        // yet) add nothing — mirroring persistAfterTurn's own guard.
        const snapshot = useGlobalAiConversationStateStore.getState();
        const id = snapshot.trackingId;
        if (
          !persistedIdsRef.current.has(id) &&
          snapshot.transcript.some((m) => m.role === 'user')
        ) {
          queryClient.setQueryData<GlobalAiConversationListItem[]>(
            CONVERSATIONS_QUERY_KEY,
            (old) => {
              if (old?.some((c) => c.id === id)) {
                return old;
              }
              const optimistic: GlobalAiConversationListItem = {
                id,
                title: fallbackConversationTitle(snapshot.transcript),
                updatedAt: new Date().toISOString(),
              };
              return old ? [optimistic, ...old] : [optimistic];
            }
          );
          // Highlight the row as the active conversation immediately (it IS
          // the chat on screen); the save re-asserts this on success.
          stateRef.current.setActiveConversationId(id);
        }
      }
      if (wasRunning && !isRunning) {
        void persistAfterTurnRef.current();
      }
    });
  }, [queryClient]);

  const newChat = useCallback(() => {
    // resetSession clears transcript (which nulls activeConversationId via
    // clearTranscript) and mints a fresh conversationTrackingIdRef. It does
    // NOT abort a running turn — callers disable New Chat while a turn is
    // running (matching the drawer).
    useGlobalAiEngineStore.getState().api?.resetSession();
  }, []);

  const selectConversation = useCallback(
    async (
      id: string
    ): Promise<{
      ok: boolean;
      reason?: 'newer_schema' | 'malformed' | 'fetch';
    }> => {
      const current = stateRef.current;
      if (id === current.activeConversationId) {
        return { ok: true };
      }
      let raw: unknown;
      try {
        const conversation = await getGlobalAiConversation(id);
        raw = conversation.payload;
      } catch {
        return { ok: false, reason: 'fetch' };
      }
      const parsed = parseConversationPayload(raw, Date.now());
      if (!parsed.ok) {
        return { ok: false, reason: parsed.reason };
      }
      persistedIdsRef.current.add(id);
      current.hydrateConversation(parsed.payload, id);
      return { ok: true };
    },
    []
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      // The row is already gone from the list (onMutate), so reset the session
      // in the same tick when the deleted chat is the one on screen —
      // otherwise its transcript would linger under a row that no longer
      // exists. On failure the row comes back but the transcript does not;
      // the conversation is intact server-side, so one click reloads it (which
      // also re-adds it to persistedIds).
      persistedIdsRef.current.delete(id);
      if (stateRef.current.activeConversationId === id) {
        newChat();
      }
      await deleteMutation(id);
    },
    [deleteMutation, newChat]
  );

  const refetchConversationList = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Stable identity: this object is a dependency of the provider's context
  // value memo — a fresh literal here would churn the context value on EVERY
  // provider render (which happens per streamed token) and re-render every
  // context consumer.
  return useMemo(
    () => ({
      conversations,
      isConversationListLoading,
      isConversationListError,
      refetchConversationList,
      newChat,
      selectConversation,
      deleteConversation,
    }),
    [
      conversations,
      isConversationListLoading,
      isConversationListError,
      refetchConversationList,
      newChat,
      selectConversation,
      deleteConversation,
    ]
  );
}
